import React, { useState } from 'react';
import {
  Typography,
  Button,
  Switch,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Box,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage, Language } from '../../contexts/LanguageContext';
import { ENVIRONMENT } from '../../common/constant/environment';
import { LANGUAGE_FLAGS } from '../../common/constant/languages';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as S from './style';

const LANGUAGE_LABEL_KEYS: Record<string, string> = {
  en: 'language.english',
  no: 'language.norwegian',
};

const Settings: React.FC = () => {
  const { user, updateLanguage } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [languageSaving, setLanguageSaving] = useState(false);
  const [languageError, setLanguageError] = useState('');
  const environment = ENVIRONMENT || 'prod';
  const patients: Array<any> = [];
  const [patientsOpen, setPatientsOpen] = useState(false);

  if (!user) {
    return null;
  }

  const handleLanguageSelect = async (lang: string) => {
    setLanguageAnchorEl(null);
    if (lang === language) return;
    setLanguageError('');
    setLanguageSaving(true);
    try {
      await updateLanguage(lang);
      setLanguage(lang as Language);
    } catch (err) {
      setLanguageError(t('settings.languageChangeFailed'));
    } finally {
      setLanguageSaving(false);
    }
  };

  return (
    <S.PageContainer>
      <S.ContentWrapper>
        <S.Header>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 2,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('btn.back')}
          </Button>
          <Typography variant="h5" fontWeight={700}>
            {t('settings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('settings.subtitle')}
          </Typography>
        </S.Header>

        <S.SettingsCard>
          {user.role === 'superadmin' && (
            <S.SettingSection>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t('settings.admin')}
              </Typography>

              <S.SettingRow>
                <S.SettingInfo>
                  <Typography variant="body1" fontWeight={500}>
                    {t('settings.environment')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {environment === 'dev' ? 'Development' : 'Production'}
                  </Typography>
                </S.SettingInfo>
              </S.SettingRow>

            </S.SettingSection>
          )}
          <S.SettingSection>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('settings.accountSettings')}
            </Typography>

            <S.SettingRow>
              <S.SettingInfo>
                  <Typography variant="body1" fontWeight={500}>
                  {t('settings.emailAddress')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </S.SettingInfo>
              <Button variant="outlined" size="small" disabled>
                {t('settings.update')}
              </Button>
            </S.SettingRow>

            <S.SettingRow>
              <S.SettingInfo>
                  <Typography variant="body1" fontWeight={500}>
                  {t('settings.password')}
                </Typography>
              </S.SettingInfo>
              <Tooltip title={t('settings.comingSoon')}>
                <span>
                  <Button variant="outlined" size="small" disabled>
                    {t('settings.change')}
                  </Button>
                </span>
              </Tooltip>
            </S.SettingRow>
          </S.SettingSection>
          {/* <S.SettingSection>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('settings.notifications')}
            </Typography>

            <S.SettingRow>
              <S.SettingInfo>
                <Typography variant="body1" fontWeight={500}>
                  {t('settings.emailNotifications')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.receiveUpdates')}
                </Typography>
              </S.SettingInfo>
              <Switch
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
            </S.SettingRow>

            <S.SettingRow>
              <S.SettingInfo>
                <Typography variant="body1" fontWeight={500}>
                  {t('settings.pushNotifications')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.receivePush')}
                </Typography>
              </S.SettingInfo>
              <Switch
                checked={pushNotifications}
                onChange={(e) => setPushNotifications(e.target.checked)}
              />
            </S.SettingRow>
          </S.SettingSection> */}
          <Divider sx={{ my: 3 }} />
          <S.SettingSection>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('settings.preferences')}
            </Typography>

            <S.SettingRow>
              <S.SettingInfo>
                <Typography variant="body1" fontWeight={500}>
                  {t('settings.language')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {LANGUAGE_FLAGS[language] ? `${LANGUAGE_FLAGS[language]} ` : ''}
                  {LANGUAGE_LABEL_KEYS[language] ? t(LANGUAGE_LABEL_KEYS[language]) : language}
                </Typography>
                {languageError && (
                  <Typography variant="body2" color="error">
                    {languageError}
                  </Typography>
                )}
              </S.SettingInfo>
              <Button
                variant="outlined"
                size="small"
                disabled={languageSaving}
                onClick={(e) => setLanguageAnchorEl(e.currentTarget)}
              >
                {languageSaving ? t('loading') : t('settings.change')}
              </Button>
              <Menu
                anchorEl={languageAnchorEl}
                open={Boolean(languageAnchorEl)}
                onClose={() => setLanguageAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 180, mt: 0.5 } }}
                MenuListProps={{ sx: { py: 0 } }}
              >
                {Object.keys(LANGUAGE_FLAGS).map((code) => (
                  <MenuItem
                    key={code}
                    onClick={() => handleLanguageSelect(code)}
                    sx={{ gap: 1.5, fontSize: 14, py: 1 }}
                  >
                    <Box component="span">{LANGUAGE_FLAGS[code]}</Box>
                    {LANGUAGE_LABEL_KEYS[code] ? t(LANGUAGE_LABEL_KEYS[code]) : code}
                    {language === code && (
                      <CheckIcon fontSize="small" sx={{ ml: 'auto', color: 'primary.main' }} />
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </S.SettingRow>

            <S.SettingRow>
              <S.SettingInfo>
                <Typography variant="body1" fontWeight={500}>
                  {t('settings.timeZone')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </Typography>
              </S.SettingInfo>
              <Button variant="outlined" size="small" disabled>
                Change
              </Button>
            </S.SettingRow>
          </S.SettingSection>
        </S.SettingsCard>

        {/* <S.SettingsCard>
          <Typography variant="h6" fontWeight={600} color="error" sx={{ mb: 2 }}>
            {t('settings.dangerZone')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settings.deleteWarning')}
          </Typography>
          <Button variant="outlined" color="error" disabled>
            {t('settings.deleteAccount')}
          </Button>
        </S.SettingsCard> */}
      </S.ContentWrapper>

      {/* Patients Dialog */}
      <Dialog open={patientsOpen} onClose={() => setPatientsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Patients</DialogTitle>
        <DialogContent>
          {patients.length === 0 ? (
            <Typography>No patients found or an error occurred.</Typography>
          ) : (
            patients.map((p: any, idx: number) => (
              <Typography key={idx} sx={{ mb: 1 }}>
                {p.id || p.pid || p.patient_id ? `${p.id || p.pid || p.patient_id} — ` : ''}{p.name || p.full_name || p.fname || p.lname || JSON.stringify(p)}
              </Typography>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatientsOpen(false)} variant="ghost">Close</Button>
        </DialogActions>
      </Dialog>
    </S.PageContainer>
  );
};

export default Settings;
